import { FC, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks/storeHooks";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS, ROUTE_QUERY_CONSTANTS } from "../../../routes";
import IsMobile from "../../../hooks/isMobile";
import { getFlatCorporateEvents } from "../../../utils/B2B/corporateEventsUtils";
import { ICorporateEvent, IFlatCorporateEvent } from "../../../interfaces/corporateEvent";
import { resetCorporateEventsApiError } from "../../../store/b2b/corporateEvent/corporateEventSlice";
import { getEventValuesCMSThunk } from "../../../store/events/eventThunk";
import MobileCorporateEvents from "./MobileCorporateEvents/MobileCorporateEvents";
import { getFromCms } from "../../../helpers/common";
import { useToast } from "../../../context/ToasterContext";
import { GridIdsEnum, GridTypesEnum } from "../../../constants/enums";
import DataTable, { iDataTableColumn } from "../../../components/reusable-components/data-table/DataTable";
import TableCard from "../../../components/reusable-components/data-table/TableCard";
import TableFooter from "../../../components/reusable-components/data-table/TableFooter";
import TableToolbar from "../../../components/reusable-components/data-table/TableToolbar";
import SaveFilterButton from "../../../components/reusable-components/save-filter-button/SaveFilterButton";
import useHiddenColumns from "../../../hooks/useHiddenColumns";
import useListTable from "../../../hooks/useListTable";
import useSavedFilters from "../../../hooks/useSavedFilters";
import { getCorporateEventFilterDefs } from "./corporateEventFilters";
import "./corporateEventsGrid.scss";

export interface ICorporateEventList {
	corporateEventList: ICorporateEvent[];
	title: string;
	queryParams: ROUTE_QUERY_CONSTANTS;
	gridId?: GridIdsEnum;
	loading?: boolean;
	columns: iDataTableColumn<IFlatCorporateEvent>[];
	showSave?: boolean;
	message?: string;
}

const CorporateEventGrid: FC<ICorporateEventList> = ({
	corporateEventList,
	title,
	queryParams,
	gridId,
	loading,
	columns,
	showSave = true,
	message = "No active RFPs yet.",
}) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isMobile = IsMobile();
	const { showToast } = useToast();
	const cms = useAppSelector((state) => state.events.cms);
	const { corporateEventsLoading, corporateEventsApiError } = useAppSelector((state) => state.corporateEvent);
	const { hiddenColumns, onToggleColumn, columnOrder, onColumnOrderChange, pinnedColumns, onTogglePin } =
		useHiddenColumns(gridId ?? `CORPORATE_${queryParams}`);

	const rows = useMemo(
		() =>
			getFlatCorporateEvents(corporateEventList, (value: string | number) =>
				getFromCms(value, cms),
			) as IFlatCorporateEvent[],
		[corporateEventList, cms],
	);
	const filterDefs = useMemo(() => getCorporateEventFilterDefs(columns.map((column) => column.key)), [columns]);
	const table = useListTable({ rows, columns, filterDefs });
	const { save, saving, canSave, clearSaved } = useSavedFilters({
		gridId,
		gridType: GridTypesEnum.CORPORATE_EVENTS,
		setItems: table.setItems,
	});

	useEffect(() => {
		dispatch(getEventValuesCMSThunk({}));
	}, []);

	useEffect(() => {
		if (!gridId) dispatch(resetCorporateEventsApiError());
	}, [gridId]);

	useEffect(() => {
		if (corporateEventsApiError) {
			showToast(corporateEventsApiError.message, undefined, "error");
		}
	}, [corporateEventsApiError]);

	const openEvent = (row: IFlatCorporateEvent) =>
		navigate(`${ROUTE_PATHS.B2B.EVENT_DETAIL.replace(":id", String(row.id))}?type=${queryParams}`);

	return (
		<>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<Box className="content">
				<Box className="pageLayoutCorporateEventsGrid">
					{isMobile ? (
						<MobileCorporateEvents
							type={queryParams}
							corporateEventsList={table.rows}
							toolbar={
								<Box sx={{ bgcolor: "#fff", borderRadius: "16px" }}>
									<TableToolbar
										{...table.toolbarProps}
										onClearAll={() => {
											table.toolbarProps.onClearAll();
											clearSaved();
										}}
										compact
										searchPlaceholder="Search events"
										filterActions={
											showSave && gridId ? (
												<SaveFilterButton onClick={() => save(table.items)} saving={saving} disabled={!canSave(table.items)} />
											) : undefined
										}
									/>
								</Box>
							}
							isLoading={corporateEventsLoading}
							message={message}
						/>
					) : (
						<TableCard
							toolbar={
								<TableToolbar
									{...table.toolbarProps}
									onClearAll={() => {
										table.toolbarProps.onClearAll();
										clearSaved();
									}}
									searchPlaceholder="Search events"
									filterActions={
										showSave && gridId ? (
											<SaveFilterButton onClick={() => save(table.items)} saving={saving} disabled={!canSave(table.items)} />
										) : undefined
									}
									columns={columns.filter((column) => !column.fixed).map(({ key, label }) => ({ key, label }))}
									hiddenColumns={hiddenColumns}
									onToggleColumn={onToggleColumn}
									columnOrder={columnOrder}
									onColumnOrderChange={onColumnOrderChange}
									pinnedColumns={pinnedColumns}
									onTogglePin={onTogglePin}
								/>
							}
						>
							<DataTable
								{...table.tableProps}
								columns={columns}
								rows={table.pageRows}
								getRowId={(row) => String(row.id)}
								rowIndicator={(row) => Boolean(row.showIndicator)}
								loading={(corporateEventsLoading || !!loading) && rows.length === 0}
								emptyMessage={rows.length === 0 ? message : "No events match these filters."}
								hiddenColumns={hiddenColumns}
								columnOrder={columnOrder}
								onColumnOrderChange={onColumnOrderChange}
								pinnedColumns={pinnedColumns}
								onRowClick={openEvent}
								minWidth={1200}
							/>
							<TableFooter {...table.footerProps} />
						</TableCard>
					)}
				</Box>
			</Box>
		</>
	);
};

export default CorporateEventGrid;
